import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient, ApiError } from '@/lib/api';
import { getComplaintMetaApi } from '@/lib/complaints-api';
import { isAdminRole, useAuthStore } from '@/lib/auth-store';
import type { MeView } from '@/lib/types';

/**
 * Issue #24：管理后台外壳允许 ADMIN/SUPER_ADMIN 或服务端能力元数据确认的投诉处理人。
 * 非管理员角色绝不凭前端角色推断放行；必须同时通过 /auth/me 与 /complaints/meta。
 */
export function RequireComplaintWorkbench({ children }: { children: ReactNode }) {
  const location = useLocation();
  const accessToken = useAuthStore((state) => state.accessToken);
  const sessionReady = useAuthStore((state) => state.sessionReady);
  const setUser = useAuthStore((state) => state.setUser);

  const accessQuery = useQuery({
    queryKey: ['complaints', 'workbench-access'],
    enabled: !!accessToken,
    queryFn: async () => {
      const me = await apiClient.get<MeView>('/auth/me');
      const meta = await getComplaintMetaApi();
      return { me, meta };
    },
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  const allowed =
    !!accessQuery.data &&
    (isAdminRole(accessQuery.data.me.role) ||
      accessQuery.data.meta.capabilities.canHandle);

  useEffect(() => {
    if (allowed && accessQuery.data) setUser(accessQuery.data.me);
  }, [accessQuery.data, allowed, setUser]);

  if (!sessionReady) {
    return <CheckingAccess />;
  }

  if (!accessToken) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (accessQuery.isError) {
    const status =
      accessQuery.error instanceof ApiError ? accessQuery.error.status : 0;
    if (status === 401 || status === 0) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname + location.search }}
        />
      );
    }
    return <Navigate to="/403" replace />;
  }

  if (!accessQuery.data) return <CheckingAccess />;
  if (!allowed) return <Navigate to="/403" replace />;
  return <>{children}</>;
}

function CheckingAccess() {
  return (
    <div
      className="flex h-screen items-center justify-center bg-slate-50 text-slate-500"
      data-testid="complaint-workbench-auth-checking"
    >
      正在验证工作台权限…
    </div>
  );
}
